"use client";

import {useCallback, useEffect, useRef, useState} from "react";
import {apiLive} from "@/lib/api";
import {VoiceOption} from "@/types/agencies";

const SEARCH_DEBOUNCE_MS = 500;

export function useVoiceCatalogFetch(searchQuery: string) {
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(true);
  const [voiceLoadFailed, setVoiceLoadFailed] = useState(false);
  const fetchGenerationRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const fetchVoices = useCallback(async (query: string, attempt = 0) => {
    clearRetry();
    const generation = ++fetchGenerationRef.current;
    setIsLoadingVoices(true);
    setVoiceLoadFailed(false);

    const params = new URLSearchParams();
    if (query.trim()) {
      params.append("text", query.trim());
    }

    const queryString = params.toString();
    const url = queryString ? `/api/voices?${queryString}` : "/api/voices";

    let shouldRetry = false;
    try {
      const response = await apiLive<VoiceOption[]>(url);
      if (generation !== fetchGenerationRef.current) return;

      if (!Array.isArray(response) || response.length === 0) {
        if (attempt < 1) {
          shouldRetry = true;
        } else {
          setVoices([]);
          setVoiceLoadFailed(!response);
        }
      } else {
        setVoices(response);
      }
    } catch (error) {
      if (generation !== fetchGenerationRef.current) return;
      if (attempt < 1) {
        shouldRetry = true;
      } else {
        console.error("[VoiceSelector] Failed to load voices", error);
        setVoiceLoadFailed(true);
        setVoices([]);
      }
    } finally {
      if (generation === fetchGenerationRef.current && !shouldRetry) {
        setIsLoadingVoices(false);
      }
    }

    if (shouldRetry && generation === fetchGenerationRef.current) {
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        void fetchVoices(query, attempt + 1);
      }, 400);
    }
  }, [clearRetry]);

  // Unmounting (e.g. closing the recommended-voices dialog) must not fire a
  // pending retry or apply an in-flight response.
  useEffect(
    () => () => {
      clearRetry();
      fetchGenerationRef.current += 1;
    },
    [clearRetry]
  );

  useEffect(() => {
    if (!searchQuery.trim()) {
      void fetchVoices(searchQuery);
      return;
    }
    const timeout = setTimeout(() => {
      fetchVoices(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [searchQuery, fetchVoices]);

  return {voices, isLoadingVoices, voiceLoadFailed, fetchVoices};
}
